import { useMemo, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { ActionIcon, Alert, Anchor, Badge, Box, Button, Card, Chip, Group, Menu, Radio, SegmentedControl, Stack, Stepper, Text, Textarea, Title } from '@mantine/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { IconDots, IconLock, IconSparkles } from '@tabler/icons-react';
import { assessArguments, dismissResponse, emptyBallot, lineSource, mapHasCorrections, mapSuggestion, moveResponse, redactor, sideOfSlot, sheetTitle, type Ballot, type FlowCell, type FlowRow, type MapArgument, type Round, type SideIndex, type Survival, extensionName } from '@flow/core';
import { ApiError } from '../../infra/api';
import { useAiAvailable } from '../ai/useAi';
import { useMapArguments } from '../ai/useDecisionAi';
import { run, useDeps, useUseCases } from '../../app/services';
import { Term } from '../knowledge/KnowledgeDrawer';

const MECHANISMS = ['magnitude', 'probability', 'timeframe', 'scope', 'reversibility', 'prerequisite', 'framework'];

const sideColor = (s: SideIndex | null) => (s === null ? 'var(--side-none)' : `var(--side-${s})`);

const STATUS = {
  alive: { color: 'green', label: 'carried through' },
  contested: { color: 'yellow', label: 'contested' },
  dead: { color: 'gray', label: 'not carried' },
  unclear: { color: 'gray', label: 'unclear from notes' },
} as const;
const ANSWERED = { yes: '✓ answered', no: '⚠ not answered', partly: '~ partly answered', unclear: '? unclear' } as const;

const SOURCE = { 'same-row': 'same row', 'other-row': 'from another row', 'cross-ex': 'cross-ex note', unknown: 'not on a row' } as const;

/**
 * One argument as the AI read it off the flow, with the judge's call on the right. Every line says where it came from
 * and jumps to that spot on the flow; responses can be moved to the argument they're really about, or dismissed.
 */
const MapCard = ({ a, others, round, side, show, survival, locked, onCall, onJump, onMove, onDismiss }: {
  a: MapArgument; others: MapArgument[]; round: Round; side: SideIndex | null; show: (s: string) => string; survival: Survival; locked: boolean;
  onCall: (s: Survival) => void; onJump: (rowId: string | null, segId: string) => void; onMove: (index: number, toRow: string) => void; onDismiss: (index: number, dismissed: boolean) => void;
}) => {
  // Where the argument itself was first written: this side's first constructive on its row.
  const caseSeg = round.format.segments.find((s) => s.role === 'constructive' && s.by !== 'both' && sideOfSlot(round, s.by) === side)?.id ?? round.format.segments[0]!.id;
  return (
    <Card className="ruled" padding="xs" data-testid="map-card">
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Box style={{ minWidth: 0, flex: 1 }}>
          <Group gap={6} wrap="nowrap">
            <Anchor component="button" type="button" fw={600} size="sm" style={{ color: sideColor(side) }} lineClamp={1} onClick={() => onJump(a.row, caseSeg)} title="Show on the flow">{a.side}: {show(a.title)}</Anchor>
            <Badge size="xs" variant="light" color={STATUS[a.status].color}>{STATUS[a.status].label}</Badge>
          </Group>
          <Text size="xs" mt={2}><b>Made:</b> {show(a.claim)}</Text>
          {a.engaged.map((e, i) => {
            const dismissed = e.judge === 'dismissed';
            const src = lineSource(a.row, e, round.format);
            return (
              <Group key={i} gap={4} wrap="nowrap" align="flex-start" data-testid="map-line" data-judge={e.judge ?? ''}>
                <Text size="xs" style={{ flex: 1, textDecoration: dismissed ? 'line-through' : undefined, opacity: dismissed ? 0.55 : 1 }}>
                  <b>Engaged</b>{' '}
                  <Badge component="button" type="button" size="xs" variant="outline" style={{ cursor: 'pointer' }} onClick={() => onJump(e.row, e.speech)} title="Show on the flow">{e.speech} ↗</Badge>{' '}
                  {show(e.response)}{' '}
                  <Text span size="xs" c={e.answered === 'no' ? 'orange' : 'dimmed'}>{ANSWERED[e.answered]}{e.answeredIn ? ` in ${e.answeredIn}` : ''}</Text>
                  {' · '}<Text span size="xs" c={src === 'same-row' ? 'dimmed' : 'blue'}>{SOURCE[src]}</Text>
                  {e.judge === 'moved' && <Text span size="xs" c="dimmed"> · moved by you</Text>}
                </Text>
                <Menu position="bottom-end" withinPortal>
                  <Menu.Target>
                    <ActionIcon size="xs" variant="subtle" color="gray" aria-label="Fix this link"><IconDots size={12} /></ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {dismissed
                      ? <Menu.Item onClick={() => onDismiss(i, false)}>Undo: it is about this argument</Menu.Item>
                      : <Menu.Item onClick={() => onDismiss(i, true)}>Not about this argument</Menu.Item>}
                    {others.length > 0 && <Menu.Label>Belongs to…</Menu.Label>}
                    {others.map((o) => <Menu.Item key={o.row} onClick={() => onMove(i, o.row)}>{o.side}: {show(o.title)}</Menu.Item>)}
                  </Menu.Dropdown>
                </Menu>
              </Group>
            );
          })}
          <Group gap={4} mt={2}>
            <Text size="xs" fw={700}>Extended</Text>
            {a.extended.map((x) => (
              <Badge key={x.speech} size="xs" variant={x.present ? 'light' : 'outline'} color={x.present ? 'green' : 'gray'} style={{ cursor: 'pointer' }} component="button" type="button" onClick={() => onJump(x.row ?? a.row, x.speech)} title="Show on the flow">
                {x.present ? '✓' : '✗'} {x.speech}
              </Badge>
            ))}
          </Group>
          {a.weighed.map((w, i) => <Text size="xs" key={i}><b>Weighed</b> <Badge size="xs" variant="outline">{w.speech}</Badge> {show(w.text)}</Text>)}
          <Text size="xs" c="dimmed" mt={2}>{show(a.why)}</Text>
        </Box>
        <SegmentedControl
          size="xs"
          disabled={locked}
          value={survival}
          onChange={(v) => onCall(v as Survival)}
          data={[{ value: 'alive', label: 'Alive' }, { value: 'unsure', label: '?' }, { value: 'dead', label: 'Out' }]}
        />
      </Group>
    </Card>
  );
};

/** Guided decision: what survived → was it answered → weighing → commit. Suggestions only; you decide. */
export const DecidePanel = ({ round, rows, cells, onDone, onJump }: { round: Round; rows: FlowRow[]; cells: FlowCell[]; onDone: () => void; onJump: (rowId: string | null, segId: string) => void }) => {
  const deps = useDeps();
  const uc = useUseCases();
  const ballot = useLiveQuery(async () => (await deps.ballots.get(round.id)) ?? emptyBallot(round.id, 0), [round.id]);
  const args = useMemo(() => assessArguments(round, [...rows].sort((a, b) => a.rank - b.rank), cells), [round, rows, cells]);
  const names = useLiveQuery(() => deps.names.get(round.id), [round.id]);
  const ai = useAiAvailable();
  const mapArguments = useMapArguments();
  const [mapping, setMapping] = useState(false);
  if (!ballot) return null;
  const map = ballot.argumentMap?.arguments ?? [];
  const show = redactor(round, names).restoreNames;
  const sideOf = (label: string): SideIndex | null => {
    const i = round.format.sideLabels.findIndex((l) => l.toUpperCase() === label.trim().toUpperCase());
    return i === 0 || i === 1 ? i : null;
  };
  const runMap = async () => {
    if (mapHasCorrections(map) && !confirm('Map again? Your fixes to the links (moved or dismissed responses) will be replaced.')) return;
    setMapping(true);
    try {
      const n = await mapArguments(round);
      if (n === 0) notifications.show({ message: 'Nothing on the flow to map yet.' });
    } catch (e) {
      notifications.show({ color: 'yellow', title: "Couldn't map the flow", message: e instanceof ApiError && e.body && typeof e.body === 'object' && 'message' in e.body ? String(e.body.message) : 'Try again, or use the checklist below.' });
    } finally {
      setMapping(false);
    }
  };

  const locked = ballot.committedAt !== null;
  const [s0, s1] = round.format.sideLabels;
  const label = (s: SideIndex | null) => (s === null ? '?' : round.format.sideLabels[s]);
  const survivalOf = (rowId: string, suggestion: Survival): Survival => ballot.assessments[rowId]?.survival ?? suggestion;
  const save = (patch: Partial<Ballot>) => void run(uc.saveBallot(round.id, patch));

  // The argument list: the AI's map when there is one, otherwise the rule-based checklist.
  const items = map.length
    ? map.map((a) => ({ rowId: a.row, side: sideOf(a.side), title: show(a.title), suggestion: mapSuggestion(a.status) }))
    : args.map((a) => ({ rowId: a.rowId, side: a.side, title: a.title, suggestion: a.suggestion }));
  const alive = items.filter((a) => survivalOf(a.rowId, a.suggestion) === 'alive');
  const step = locked ? 4 : ballot.winnerSide !== null ? 3 : ballot.weighing.winner !== null ? 2 : Object.keys(ballot.assessments).length ? 1 : 0;

  return (
    <Box p="md" maw={960} mx="auto">
      <Stepper active={step} size="xs" mb="md" allowNextStepsSelect={false}>
        <Stepper.Step label="What survived?" />
        <Stepper.Step label="Answered?" />
        <Stepper.Step label="Weighing" />
        <Stepper.Step label="Decide" />
      </Stepper>

      {locked && (
        <Alert icon={<IconLock size={16} />} color="gray" mb="md" title={`Decision committed: ${label(ballot.winnerSide as SideIndex)} wins`}>
          <Group justify="space-between">
            <Text size="sm">Your decision is locked. Write the ballot next.</Text>
            <Group gap="xs">
              <Button size="xs" variant="subtle" onClick={() => void run(uc.reopenDecision(round.id))}>Reopen decision</Button>
              <Button size="xs" variant="filled" onClick={onDone}>Write ballot →</Button>
            </Group>
          </Group>
        </Alert>
      )}

      <Title order={3} size="h5" mb={4}>1. Which arguments made it to the end?</Title>
      <Text size="sm" c="dimmed" mb="sm">
        {round.format.event === 'pf' || !round.format.event
          ? <>In PF an argument only counts if its team <Term q="extend">extended</Term> it in <b>both</b> <Term>Summary</Term> and <Term q="Final focus">Final Focus</Term>. </>
          : <>{round.format.extensionNote} </>}
        These suggestions come from your flow. Confirm or change each one.
      </Text>
      {ai && !locked && (
        <Group gap="xs" mb="sm">
          <Button size="xs" leftSection={<IconSparkles size={14} />} loading={mapping} onClick={() => void runMap()} data-testid="map-arguments">
            {map.length ? 'Map again' : 'Map the arguments with AI'}
          </Button>
          <Text size="xs" c="dimmed">{map.length ? `Mapped ${new Date(ballot.argumentMap!.createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}. It describes the flow; the calls are yours.` : 'Traces each argument: who answered it, whether it was carried through, and any weighing. About 20 seconds.'}</Text>
        </Group>
      )}
      {map.length > 0 && (
        <Stack gap={6} mb="lg">
          {map.map((a) => (
            <MapCard
              key={a.row}
              a={a}
              others={map.filter((o) => o.row !== a.row)}
              round={round}
              onJump={onJump}
              onMove={(i, to) => save({ argumentMap: { ...ballot.argumentMap!, arguments: moveResponse(map, a.row, i, to) } })}
              onDismiss={(i, d) => save({ argumentMap: { ...ballot.argumentMap!, arguments: dismissResponse(map, a.row, i, d) } })}
              side={sideOf(a.side)}
              show={show}
              survival={survivalOf(a.row, mapSuggestion(a.status))}
              locked={locked}
              onCall={(v) => save({ assessments: { ...ballot.assessments, [a.row]: { survival: v, note: ballot.assessments[a.row]?.note ?? '' } } })}
            />
          ))}
        </Stack>
      )}
      {map.length === 0 && args.length === 0 && <Text size="sm" c="dimmed" mb="md">Nothing on the flow yet. Arguments show up here once their first speech has notes.</Text>}
      {map.length === 0 && <Stack gap={6} mb="lg">
        {args.map((a) => {
          const s = survivalOf(a.rowId, a.suggestion);
          const sheet = round.format.sheets.find((x) => x.id === a.sheetId);
          return (
            <Card key={a.rowId} className="ruled" padding="xs" data-testid="arg-card">
              <Group justify="space-between" wrap="nowrap" align="flex-start">
                <Box style={{ minWidth: 0 }}>
                  <Text fw={600} size="sm" style={{ color: sideColor(a.side) }} lineClamp={1}>{label(a.side)}: {a.title}</Text>
                  <Group gap={4} mt={2}>
                    {a.carried.map((c) => (
                      <Badge key={c.role} size="xs" variant={c.present ? 'light' : 'outline'} color={c.present ? 'green' : 'gray'}>{c.present ? '✓' : '✗'} {extensionName(c.role, c.segmentId, round.format.event)}</Badge>
                    ))}
                    <Badge size="xs" variant="outline" color={a.answered ? 'orange' : 'gray'}>{a.answered ? 'answered' : 'not answered'}</Badge>
                    {a.markedTurn && <Badge size="xs" variant="light" color="violet">turn</Badge>}
                    {a.concededInCross && <Badge size="xs" variant="light" color="yellow">conceded in cross</Badge>}
                    {sheet && <Text size="xs" c="dimmed">on {sheetTitle(round, sheet.title)}</Text>}
                  </Group>
                  <Text size="xs" c="dimmed" mt={2}>{a.why}</Text>
                </Box>
                <SegmentedControl
                  size="xs"
                  disabled={locked}
                  value={s}
                  onChange={(v) => save({ assessments: { ...ballot.assessments, [a.rowId]: { survival: v as Survival, note: '' } } })}
                  data={[{ value: 'alive', label: 'Alive' }, { value: 'unsure', label: '?' }, { value: 'dead', label: 'Out' }]}
                />
              </Group>
            </Card>
          );
        })}
      </Stack>}

      <Title order={3} size="h5" mb={4}>2. Who is winning each live argument?</Title>
      <Text size="sm" c="dimmed" mb="sm">
        For each argument still alive: did the other side answer it, and was that answer extended too? A <Term q="turn">turn</Term> is <Term q="offense">offense</Term> for the team that made it.
        <Term q="defense"> Defense</Term> alone rarely wins a round.
      </Text>
      <Text size="sm" mb="lg">
        Still alive: {alive.length === 0 ? 'none yet' : alive.map((a) => (
          <Badge key={a.rowId} mr={4} variant="light" style={{ color: sideColor(a.side) }}>{label(a.side)}: {a.title.slice(0, 30)}</Badge>
        ))}
      </Text>

      <Title order={3} size="h5" mb={4}>3. <Term q="weighing">Weighing</Term>: which argument matters more?</Title>
      <Text size="sm" c="dimmed" mb="sm">What reasons did each team give for why their argument outweighs? If nobody weighed, you'll have to compare the arguments yourself and explain how in the RFD.</Text>
      <Stack gap="xs" mb="sm">
        {([0, 1] as SideIndex[]).map((side) => (
          <Group key={side} gap="xs" wrap="wrap">
            <Text size="sm" fw={600} w={60} style={{ color: sideColor(side) }}>{label(side)}</Text>
            <Chip.Group multiple value={side === 0 ? ballot.weighing.side0 : ballot.weighing.side1} onChange={(v) => save({ weighing: { ...ballot.weighing, [side === 0 ? 'side0' : 'side1']: v } })}>
              <Group gap={4}>
                {MECHANISMS.map((m) => <Chip key={m} value={m} size="xs" disabled={locked}>{m}</Chip>)}
              </Group>
            </Chip.Group>
          </Group>
        ))}
      </Stack>
      <Radio.Group
        label="Who won the weighing?"
        value={ballot.weighing.winner ?? ''}
        onChange={(v) => save({ weighing: { ...ballot.weighing, winner: v as 'side0' | 'side1' | 'none' } })}
        mb="xs"
      >
        <Group mt={4}>
          <Radio value="side0" label={s0} disabled={locked} />
          <Radio value="side1" label={s1} disabled={locked} />
          <Radio value="none" label="Nobody weighed / a wash" disabled={locked} />
        </Group>
      </Radio.Group>
      <Textarea classNames={{ input: 'writing' }}
        placeholder="Weighing notes (optional): why one argument matters more"
        autosize
        minRows={2}
        disabled={locked}
        defaultValue={ballot.weighing.note}
        onBlur={(e) => save({ weighing: { ...ballot.weighing, note: e.currentTarget.value } })}
        mb="lg"
      />

      <Title order={3} size="h5" mb={4}>4. Decide</Title>
      <Text size="sm" c="dimmed" mb="sm">Usually, the team winning the most important live argument wins. Commit before writing the ballot. That way your reasons follow your decision, not the other way around.</Text>
      <Group>
        <Radio.Group value={ballot.winnerSide === null ? '' : String(ballot.winnerSide)} onChange={(v) => save({ winnerSide: Number(v) as SideIndex })}>
          <Group>
            <Radio value="0" label={`${s0} wins`} disabled={locked} data-testid="winner-0" />
            <Radio value="1" label={`${s1} wins`} disabled={locked} data-testid="winner-1" />
          </Group>
        </Radio.Group>
        <Button
          variant="filled"
          leftSection={<IconLock size={14} />}
          disabled={locked || ballot.winnerSide === null}
          onClick={async () => {
            if (ballot.winnerSide === null) return;
            if (await run(uc.commitDecision(round.id, ballot.winnerSide as SideIndex))) onDone();
          }}
          data-testid="commit"
        >
          Commit decision
        </Button>
      </Group>
    </Box>
  );
};
